import { useDynamicConfig } from '@statsig/react-bindings';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { useDiscountContext } from '@/app/[locale]/store/_contexts/DiscountProvider';
import {
  OR4_JADE_PDP_PATHNAMES,
  OR4_PDP_PATHNAMES,
} from '@/app/consts/pathnames';
import { RingStyleEnum } from '@/app/enums/RingStyleEnum';
import type { RingStyle } from '@/app/types/ring';

import getDiscountMessage from './discount/getDiscountMessage';
import showDiscountMessage from './discount/showDiscountMessage';
import type { UiDiscount } from './discount/types';
import useFormattedDiscountPreview from './useFormattedDiscountPreview';

export interface UseDiscountMessageProps {
  gen3WizardConsumer?: boolean;
  isAccessory: boolean;
}
interface UseDiscountMessage {
  displayMessage: string | null | ReactNode;
}

const hasSitewideDiscount = (sitewideDiscounts: string | string[]): boolean => {
  if (Array.isArray(sitewideDiscounts)) {
    return sitewideDiscounts.length > 0;
  }
  return Boolean(sitewideDiscounts);
};

const PRODUCT_CATEGORY_BY_RING_STYLE: Record<RingStyle, string> = {
  [RingStyleEnum.Heritage]: 'oura-gen-3',
  [RingStyleEnum.Horizon]: 'oura-gen-3',
  [RingStyleEnum.OuraRing4]: 'oura-ring-4',
  [RingStyleEnum.Ceramic]: 'oura-ring-4-ceramic',
};

const ringStyleFromPathname = (pathname: string): RingStyleEnum | undefined => {
  if (OR4_PDP_PATHNAMES.some((path) => pathname.includes(path))) {
    return RingStyleEnum.OuraRing4;
  } else if (OR4_JADE_PDP_PATHNAMES.some((path) => pathname.includes(path))) {
    return RingStyleEnum.Ceramic;
  } else if (pathname.includes('/oura-gen3/heritage')) {
    return RingStyleEnum.Heritage;
  } else if (pathname.includes('/oura-gen3/horizon')) {
    return RingStyleEnum.Horizon;
  }
  return undefined;
};

const highestDiscountMessageAppliesToCurrentPage = (
  pathname: string,
  enabledByCategory: Record<string, boolean>,
): boolean => {
  const ringStyle = ringStyleFromPathname(pathname);
  if (ringStyle) {
    const category = PRODUCT_CATEGORY_BY_RING_STYLE[ringStyle];
    return enabledByCategory[category] ?? false;
  }
  return false;
};

const useDiscountMessage = ({
  gen3WizardConsumer = false,
  isAccessory,
}: UseDiscountMessageProps): UseDiscountMessage => {
  const pathname = usePathname();
  const t = useTranslations();

  const sitewideDiscountConfig = useDynamicConfig('sitewide_discount');
  const sitewideDiscounts = (sitewideDiscountConfig.get('sitewideDiscount') ??
    '') as string | string[];
  const highestDiscountEyebrowProperties = sitewideDiscountConfig.get(
    'enableHighestDiscountEyebrow',
    {},
  ) as Record<string, boolean>;

  const showHighestDiscountEyebrow =
    hasSitewideDiscount(sitewideDiscounts) &&
    highestDiscountMessageAppliesToCurrentPage(
      pathname,
      highestDiscountEyebrowProperties,
    );

  const discountConfig = useDynamicConfig('discount_user_interface');
  const statsigDiscountArray = discountConfig.get(
    'discountArray',
    [],
  ) as UiDiscount[];

  const {
    appliedDiscountCode,
    discount,
    discountIsLoading,
    discountIsSuccess,
    promotionCodes,
    urlDiscount,
    urlDiscountIsSuccess,
    urlPromotionData,
  } = useDiscountContext();

  // determine whether or not to show discount eyebrow
  const showUrlDiscount = showDiscountMessage({
    isAccessory,
    gen3WizardConsumer,
    urlDiscount,
    urlDiscountIsSuccess,
    urlPromotionData,
  });
  // get the appropriate discount message to display
  const discountMessage = getDiscountMessage({
    appliedDiscountCode,
    campaign: discount?.content?.discount?.campaign,
    discount: showUrlDiscount ? urlDiscount : discount,
    discountIsLoading,
    discountIsSuccess,
    promotionCodes,
    showHighestDiscountEyebrow,
    showUrlDiscount,
    statsigDiscountArray,
    urlPromotionData,
  });

  // format price if applicable
  const { isLoading: formattedAmountIsLoading, formattedAmount } =
    useFormattedDiscountPreview(appliedDiscountCode);

  let displayMessage = null;

  // t() the Phrase string for display in multiple languages
  if (discountMessage) {
    if (!formattedAmountIsLoading) {
      displayMessage = t.rich(discountMessage, { amount: formattedAmount });
    } else {
      displayMessage = t(discountMessage);
    }
  }

  return { displayMessage };
};

export default useDiscountMessage;
